export type PlatformScanResult = {
  rawValue: string;
  format?: string;
};

export type PlatformScanOnceOptions = {
  /**
   * Web preview element, owned by the caller.
   * The native scanner opens its own camera UI and ignores this.
   */
  video?: HTMLVideoElement;
  signal?: AbortSignal;
  /** Prefer rear camera on mobile. */
  facingMode?: "environment" | "user";
  /** Poll interval for BarcodeDetector (ms). */
  pollMs?: number;
};

const WEB_BARCODE_FORMATS = [
  "qr_code",
  "code_128",
  "code_39",
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "itf",
  "codabar",
  "data_matrix",
];

function hasBarcodeDetector(): boolean {
  return typeof window !== "undefined" && typeof window.BarcodeDetector === "function";
}

function hasGetUserMedia(): boolean {
  return (
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

/** Web: getUserMedia + BarcodeDetector. Native scan is `platform/capacitor/nativeScanner`. */
export function webScannerIsAvailable(): boolean {
  return hasGetUserMedia() && hasBarcodeDetector();
}

export function webCameraIsAvailable(): boolean {
  return hasGetUserMedia();
}

/**
 * Start camera on `video`, resolve first barcode, then stop tracks.
 * Rejects with `DOMException` name `AbortError` when signal aborts / unmount.
 */
export async function webScanOnce(
  options: PlatformScanOnceOptions,
): Promise<PlatformScanResult> {
  if (!webScannerIsAvailable()) {
    throw new Error("Scanner không khả dụng trên thiết bị này.");
  }

  const { video, signal, facingMode = "environment", pollMs = 280 } = options;
  if (!video) {
    throw new Error("Thiếu khung xem camera.");
  }
  const Detector = window.BarcodeDetector!;
  const detector = new Detector({ formats: WEB_BARCODE_FORMATS });

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  });

  const stop = () => {
    for (const track of stream.getTracks()) track.stop();
    video.srcObject = null;
  };

  if (signal?.aborted) {
    stop();
    throw new DOMException("Aborted", "AbortError");
  }

  video.srcObject = stream;
  video.setAttribute("playsinline", "true");
  video.muted = true;
  await video.play();

  return new Promise<PlatformScanResult>((resolve, reject) => {
    let settled = false;

    const finishOk = (result: PlatformScanResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const finishErr = (err: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };

    const onAbort = () => finishErr(new DOMException("Aborted", "AbortError"));
    signal?.addEventListener("abort", onAbort, { once: true });

    const timer = setInterval(() => {
      if (settled || signal?.aborted) return;
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
      void detector
        .detect(video)
        .then((codes) => {
          const hit = codes.find((c) => c.rawValue?.trim());
          if (hit) finishOk({ rawValue: hit.rawValue.trim(), format: hit.format });
        })
        .catch(() => {
          /* frame skipped — keep polling */
        });
    }, pollMs);

    function cleanup() {
      clearInterval(timer);
      stop();
      signal?.removeEventListener("abort", onAbort);
    }
  });
}

/** Read the first barcode in a still image. Does not open the camera. */
export async function webScanImage(file: Blob): Promise<PlatformScanResult> {
  if (!hasBarcodeDetector()) {
    throw new Error("Không đọc được mã từ ảnh trên trình duyệt này.");
  }
  const Detector = window.BarcodeDetector!;
  const detector = new Detector({ formats: WEB_BARCODE_FORMATS });
  const codes = await detector.detect(file);
  const hit = codes.find((code) => code.rawValue?.trim());
  if (!hit) throw new Error("Không đọc được mã trong ảnh.");
  return { rawValue: hit.rawValue.trim(), format: hit.format };
}
